/** Root: session gate → data → routes. */
import { useEffect, useState } from 'react';
import { SessionProvider, useSession } from './state/session.tsx';
import { AppDataProvider, useAppData } from './state/app-data.tsx';
import { TutorProvider } from './state/tutor.tsx';
import { RouterProvider, lessonIdFromPath, routes, useRouter } from './state/router.tsx';
import { AppShell } from './ui/AppShell.tsx';
import { DEFAULT_EYE_COLOR, RoboCat, RoboCatColorContext } from './ui/RoboCat.tsx';
import { languageInfo } from './ui/course.ts';
import { AuthScreen } from './screens/AuthScreen.tsx';
import { HomeScreen } from './screens/HomeScreen.tsx';
import { WordsScreen } from './screens/WordsScreen.tsx';
import { ChatScreen } from './screens/ChatScreen.tsx';
import { LeagueScreen } from './screens/LeagueScreen.tsx';
import { ProfileScreen } from './screens/ProfileScreen.tsx';
import { CourseSheet } from './screens/CourseSheet.tsx';
import { LessonScreen } from './screens/lesson/LessonScreen.tsx';
import { QuickScreen } from './screens/QuickScreen.tsx';
import { PlacementScreen } from './screens/PlacementScreen.tsx';
import { AchievementUnlock } from './ui/Badges.tsx';

function Splash({ message }: { message: string }) {
  return (
    <div className="center-screen">
      <div className="stack">
        <RoboCat size={96} state="thinking" />
        <p className="t-meta">{message}</p>
      </div>
    </div>
  );
}

const SECTION_PATHS: readonly string[] = [
  routes.home,
  routes.words,
  routes.chat,
  routes.league,
  routes.profile,
  routes.quick,
  routes.placement,
];

function Screen({ path, onOpenCourses }: { path: string; onOpenCourses: () => void }) {
  const lessonId = lessonIdFromPath(path);
  if (lessonId) return <LessonScreen lessonId={lessonId} />;
  switch (path) {
    case routes.quick:
      return <QuickScreen />;
    case routes.placement:
      return <PlacementScreen />;
    case routes.words:
      return <WordsScreen />;
    case routes.chat:
      return <ChatScreen />;
    case routes.league:
      return <LeagueScreen />;
    case routes.profile:
      return <ProfileScreen />;
    default:
      return <HomeScreen onOpenCourses={onOpenCourses} />;
  }
}

function Routes() {
  const { path, navigate } = useRouter();
  const [coursesOpen, setCoursesOpen] = useState(false);
  const lessonId = lessonIdFromPath(path);
  const openCourses = () => setCoursesOpen(true);

  useEffect(() => {
    if (lessonId === null && !SECTION_PATHS.includes(path)) navigate(routes.home, { replace: true });
  }, [path, lessonId, navigate]);

  return (
    <AppShell immersive={lessonId !== null || path === routes.quick || path === routes.placement} onOpenCourses={openCourses}>
      <Screen path={path} onOpenCourses={openCourses} />
      {coursesOpen && <CourseSheet onClose={() => setCoursesOpen(false)} />}
      <AchievementUnlock />
    </AppShell>
  );
}

function SignedIn() {
  const { status, error, data, activeCourse, refresh } = useAppData();
  const language = languageInfo(activeCourse?.lang);

  useEffect(() => {
    document.documentElement.classList.remove('th-cn', 'th-tr', 'th-en');
    document.documentElement.classList.add(language.theme);
  }, [language.theme]);

  if (status === 'loading' || !data) {
    if (status === 'error') {
      return (
        <div className="center-screen">
          <div className="stack">
            <RoboCat size={96} state="sad" />
            <p>Не удалось загрузить данные. {error}</p>
            <button type="button" className="btn btn--secondary" onClick={() => void refresh()}>
              Попробовать ещё раз
            </button>
          </div>
        </div>
      );
    }
    return <Splash message="Загружаем ваши уроки…" />;
  }

  return (
    <RoboCatColorContext.Provider value={data.profile.settings.eyeColor ?? DEFAULT_EYE_COLOR}>
      <TutorProvider key={activeCourse?.lang ?? 'none'} lang={activeCourse?.lang ?? 'none'} languageRu={language.adjectiveRu}>
        <Routes />
      </TutorProvider>
    </RoboCatColorContext.Provider>
  );
}

function Gate() {
  const { session, loading } = useSession();
  if (loading) return <Splash message="Сөйле" />;
  if (!session) return <AuthScreen />;
  return (
    <AppDataProvider key={session.user.id} userId={session.user.id}>
      <SignedIn />
    </AppDataProvider>
  );
}

export function App() {
  return (
    <SessionProvider>
      <RouterProvider>
        <Gate />
      </RouterProvider>
    </SessionProvider>
  );
}
